import type { VoucherResponse } from '@/api/model/voucherResponse';
import type { VoucherUpdate } from '@/api/model/voucherUpdate';
import { useUpdateVoucher } from '@/api/vouchers/vouchers';
import { convertDateTimeLocalToISO, formatDateTimeLocal } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/api-error';
import React, { useEffect } from 'react';
import { useForm, useWatch, type UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { VoucherForm, type VoucherFormData } from './VoucherForm';

interface VoucherEditFormProps {
  voucher: VoucherResponse;
  onSuccess?: () => void;
  onCancel?: () => void;
  onFormDataChange?: (data: VoucherFormData) => void;
  hideImagesField?: boolean;
  onFormInstance?: (form: UseFormReturn<VoucherFormData>) => void;
}

export const VoucherEditForm: React.FC<VoucherEditFormProps> = ({
  voucher,
  onSuccess,
  onCancel,
  onFormDataChange,
  hideImagesField = false,
  onFormInstance,
}) => {
  const navigate = useNavigate();

  const form = useForm<VoucherFormData>({
    defaultValues: {
      title: voucher.title,
      provider_id: voucher.provider.id,
      description: voucher.description || '',
      conditions: voucher.conditions || '',
      value: voucher.value,
      valid_from: formatDateTimeLocal(voucher.valid_from),
      valid_until: formatDateTimeLocal(voucher.valid_until),
      max_redemptions: voucher.max_redemptions || undefined,
      status: voucher.status,
      redemption_type: voucher.redemption_type,
      images: voucher.images || [],
    },
  });

  const updateVoucherMutation = useUpdateVoucher();

  const watchedData = useWatch({ control: form.control });

  // Watch for form changes and update preview
  useEffect(() => {
    if (!onFormDataChange || !watchedData) return;
    onFormDataChange(watchedData as VoucherFormData);
  }, [onFormDataChange, watchedData]);

  // Expose form instance to parent when requested
  useEffect(() => {
    if (onFormInstance) {
      onFormInstance(form);
    }
  }, [form, onFormInstance]);

  const onSubmit = (data: VoucherFormData) => {
    const normalizedMaxRedemptions = typeof data.max_redemptions === 'number' ? data.max_redemptions : null;

    const voucherData: VoucherUpdate = {
      title: data.title,
      description: data.description || undefined,
      conditions: data.conditions || undefined,
      value: Number(data.value),
      valid_from: convertDateTimeLocalToISO(data.valid_from),
      valid_until: convertDateTimeLocalToISO(data.valid_until),
      max_redemptions: normalizedMaxRedemptions,
      status: data.status,
      redemption_type: data.redemption_type,
      images: data.images && data.images.length > 0 ? data.images : undefined,
    };

    updateVoucherMutation
      .mutateAsync({
        voucherId: voucher.id,
        data: voucherData,
      })
      .then(() => {
        toast.success('Gutschein erfolgreich aktualisiert');
        onSuccess?.();
      })
      .catch(error => {
        toast.error(getApiErrorMessage(error, 'Aktualisieren des Gutscheins fehlgeschlagen'));
      });
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/admin/vouchers');
    }
  };

  return (
    <VoucherForm
      form={form}
      onSubmit={onSubmit}
      isSubmitting={updateVoucherMutation.isPending}
      submitText='Gutschein aktualisieren'
      onCancel={handleCancel}
      cancelText='Abbrechen'
      showImagesField={!hideImagesField}
    />
  );
};
